import React from 'react';
import { motion } from 'framer-motion';
import { Play, Target, MessageSquare, Briefcase, Video, Presentation, BarChart, Brain } from 'lucide-react';
import Button from '@/components/ui/Button';

const demoFeatures = [
  {
    icon: <Target className="w-6 h-6 text-orcsys-500" />,
    title: "Site de démo immersif",
    description: "Expérience autour du prototype, inscriptions, liste d'attente"
  },
  {
    icon: <MessageSquare className="w-6 h-6 text-orcsys-500" />,
    title: "Formulaire de feedback intégré"
  },
  {
    icon: <Briefcase className="w-6 h-6 text-orcsys-500" />,
    title: "Investor Deck Starter",
    description: "Slides prêtes à pitcher"
  },
  {
    icon: <Video className="w-6 h-6 text-orcsys-500" />,
    title: "Mode \"Démo Pitch\"",
    description: "Parcours scénarisé"
  },
  {
    icon: <BarChart className="w-6 h-6 text-orcsys-500" />,
    title: "Dashboard \"Proof Board\" enrichi"
  },
  {
    icon: <Brain className="w-6 h-6 text-orcsys-500" />,
    title: "Intégrations IA/API légères"
  }
];

const DemoSection = () => {
  return (
    <section className="py-20 lg:py-24 bg-slate-50">
      <div className="container px-4 mx-auto">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Convaincre utilisateurs et investisseurs
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-6">
              Un bon prototype ne se regarde pas. Il se présente.
            </p>
            <p className="text-lg text-slate-700 max-w-3xl mx-auto">
              Nous concevons des prototypes qui ne se contentent pas d'exister — ils séduisent, rassurent et convainquent.
              Vous repartez avec une expérience complète de démonstration, pensée pour transformer vos tests en traction.
            </p>
          </motion.div>

          <div className="mb-8">
            <p className="text-center text-slate-600 mb-6">
              Selon votre formule, nous pouvons inclure :
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {demoFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-slate-100"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-orcsys-50 flex items-center justify-center flex-shrink-0">
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-slate-900 mb-1">
                        {feature.title}
                      </h4>
                      {feature.description && (
                        <p className="text-sm text-slate-600">
                          {feature.description}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-orcsys-500 to-orcsys-600 rounded-2xl p-8 md:p-12 text-center text-white"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Faites l'expérience d'une de nos démos
            </h3>
            <p className="text-lg text-white/90 mb-6 max-w-2xl mx-auto">
              Testez une démo interactive et découvrez la différence entre une simple maquette et un prototype stratégique.
              Fluidité, logique, émotion : tout est conçu pour convaincre.
            </p>
            <Button.Link
              href="/demo-login"
              className="bg-white text-orcsys-600 hover:bg-white/90"
              size="lg"
            >
              <Play className="w-5 h-5 mr-2" />
              Explorer une démo Orcsys
            </Button.Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DemoSection;

